import GraphBase, { DataPoint, IGraphBaseProps } from "./GraphBase";

interface ILineGraphProps extends IGraphBaseProps {
    lines: ILineGraphLine[];
}

interface ILineGraphLine {
    label: string;
    color: string;
    dotColor?: string;
    points: DataPoint[];
}

class LineGraph extends GraphBase<ILineGraphProps> {
    constructor(props: Readonly<ILineGraphProps>) {
        super(props);

        this.state = {};
    }

    public componentDidMount() {
        super.componentDidMount();
    }

    /*
    public componentDidUpdate() {
        super.componentDidUpdate();
    }
    */

    protected redrawInner(ctx: CanvasRenderingContext2D) {
        ctx.save();

        const labelMetrics = this.computeLabels(ctx);

        this.drawBackground(ctx);

        this.drawLineLabels(ctx, labelMetrics);

        this.clipInner(ctx);
        this.drawGridlinesY(ctx);
        this.drawGridlinesX(ctx);

        ctx.translate(0.5, 0.5);
        for (const line of this.props.lines) {
            const dots = [];
            ctx.beginPath();

            for (let i = 0; i < line.points.length; i++) {
                const { x, y } = this.pointToPx(line.points[i]);
                if (line.dotColor) {
                    dots.push({ x, y });
                }

                if (i === 0) {
                    ctx.moveTo(x, y - 0.5);
                }
                else {
                    ctx.lineTo(x, y - 0.5);
                }
            }

            ctx.strokeStyle = line.color || "#000";
            ctx.lineWidth = 2;
            ctx.stroke();

            if (line.dotColor) {
                ctx.fillStyle = line.dotColor;
                ctx.beginPath();
                for (const dot of dots) {
                    ctx.arc(dot.x, dot.y, 3, 0, 2 * Math.PI, false);
                    ctx.closePath();
                }
                ctx.fill();
            }
        }


        // Restore clip region
        ctx.restore();

        this.drawFrame(ctx);

        this.drawTicksY(ctx);
        this.drawTicksX(ctx);
        this.drawTickLabelsY(ctx, "left");
        this.drawTickLabelsX(ctx, "bottom");

        this.drawAxisTitles(ctx);

    }

    private drawLineLabels(ctx: CanvasRenderingContext2D, metrics: { width: number, height: number, lineHeight: number }) {
        const { top, right } = this.getPadding();
        const { height } = this.getInnerBounds();
        const { width } = this.getOuterBounds();

        // Figure out where the labels should go
        let yTop = Math.round(((height) / 2) - (metrics.height / 2)) + top + (metrics.lineHeight / 2);

        ctx.save();

        ctx.textBaseline = "middle";
        ctx.textAlign = "left";
        ctx.font = this.getStyle("label-font", "arial 0.9em");

        ctx.strokeStyle = "#000";
        ctx.lineWidth = 1;

        ctx.beginPath();
        for (const line of this.props.lines) {
            if (!line.label) {
                continue;
            }

            const lines = line.label.split("\n");

            // Text
            let textOffset = 0;
            for (const label of lines) {
                ctx.beginPath();
                ctx.fillStyle = this.getStyle("label-color", "#000");
                ctx.fillText(label, (width - right) + 16 + 12 + 8, yTop + textOffset);
                textOffset += Math.round(metrics.lineHeight + 2);
            }

            // Color square
            const textCenterOffset = Math.round((0.5 * lines.length - 1) * (metrics.lineHeight + 2));
            ctx.translate(0.5, 0.5);
            ctx.fillStyle = line.color;
            ctx.rect((width - right) + 16, Math.round(yTop + textCenterOffset), 12, 12);
            ctx.fill();
            ctx.stroke();
            ctx.translate(-0.5, -0.5);

            yTop += textOffset + 12;
        }

        ctx.restore();
    }

    private computeLabels(ctx: CanvasRenderingContext2D) {
        let maxLabelWidth = 0;
        let totalHeight = 0;
        let maxLineHeight = 0;

        ctx.font = this.getStyle("label-font", "arial 0.9em");

        for (const line of this.props.lines) {
            const lines = line.label.split("\n");
            for (const label of lines) {
                const metrics = ctx.measureText(label);
                maxLabelWidth = Math.max(metrics.width, maxLabelWidth);
                maxLineHeight = Math.max(metrics.actualBoundingBoxDescent + metrics.actualBoundingBoxAscent + 1, maxLineHeight);

                totalHeight += metrics.actualBoundingBoxAscent + metrics.actualBoundingBoxDescent + 1;
            }

            if (totalHeight > 0) {
                totalHeight += 12;
            }
        }

        this.setExtraPadding({ right: Math.round(maxLabelWidth) + 16 + 12 + 8 }, "set");

        return {
            width: maxLabelWidth,
            height: totalHeight,
            lineHeight: maxLineHeight
        }
    }
}

export default LineGraph;
